import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';

import { store } from 'redux/store';
import {
  selectCPMFTrackingForecastSubpage,
  selectCPMFTrackingForecastLocationId,
  selectCPMFTrackingForecastMultiLocationIds,
  selectCPMFTrackingForecastMultiModelIds,
  selectCPMFTrackingForecastMembers,
  selectCPMFTrackingForecastRounds,
  selectCPMFTrackingForecastVariable,
  selectCPMFTrackingForecastForecastDate,
  selectCPMFTrackingForecastTimeFrequency,
  selectCPMFTrackingForecastModelId,
  setTimeFrequency,
  setModelId,
  setMultiModelIds,
  setMultiGroupIds,
  setMultiLocationIds,
  setLocationId,
  setVariable,
  setForecastDate,
  setRounds,
  setMembers,
  setAutoQuerying,
} from 'redux/reducers/meteorologyFieldsTrackingForecastControlPanelReducer';
import {
  isPristine,
  resolveTimeFrequency,
  resolveModelId,
  firstModelId,
  resolveVariable,
  mostRecentDate,
  mostRecentAvailableDate,
  allGroupIds,
  allLocationIds,
  firstLocationOfFirstGroup,
  allMemberIds,
} from 'helper/Meteorology/Fields/TrackingForecast/DetailedView/openDefaults';

import useGroupedLocations from './useGroupedLocations';
import useTimeFrequency from './useTimeFrequency';
import useModelOptions from './useModelOptions';
import useVariableOptions from './useVariableOptions';
import useForecastDateOptions from './useForecastDateOptions';
import useMemberOptions from './useMemberOptions';
import useListPreferences from './useListPreferences';
import useSelectPreference from './useSelectPreference';
import { useChartRequestTriggerValue } from './chartRequestTriggerStore';

/**
 * Defaults ao abrir a Visão Detalhada (#1822, Área 3) — Fields.
 *
 * A cascata NÃO é uma máquina de estados por modo: cada efeito abaixo dispara
 * sozinho quando (a) o auto-preenchimento está ligado, (b) a lista dele chegou
 * e (c) o campo dele ainda está vazio. A ORDEM emerge das option hooks, que só
 * consultam a API quando as dependências upstream já estão setadas — por isso
 * o Multi-Modelos preenche a data antes do modelo sem nenhum código de ordem.
 *
 * `autoFilling` existe porque `isPristine` deixa de valer assim que o primeiro
 * campo é despachado; ele é o latch que mantém a cascata viva até a consulta.
 */
export default function useApplyOpenDefaults() {
  const subpage = useSelector(selectCPMFTrackingForecastSubpage);
  const locationId = useSelector(selectCPMFTrackingForecastLocationId);
  const multilocationIds = useSelector(selectCPMFTrackingForecastMultiLocationIds);
  const multimodelIds = useSelector(selectCPMFTrackingForecastMultiModelIds);
  const members = useSelector(selectCPMFTrackingForecastMembers);
  const rounds = useSelector(selectCPMFTrackingForecastRounds);
  const variable = useSelector(selectCPMFTrackingForecastVariable);
  const forecastDate = useSelector(selectCPMFTrackingForecastForecastDate);
  const timeFrequency = useSelector(selectCPMFTrackingForecastTimeFrequency);
  const modelId = useSelector(selectCPMFTrackingForecastModelId);
  // A função de trigger vive fora do Redux (módulo `chartRequestTriggerStore`),
  // pois função não-serializável quebra o serializableCheck — mesma fonte que o
  // `useSelectPreference` consome. O #1822 (auto-consulta ao abrir) lê daqui.
  const triggerRequest = useChartRequestTriggerValue();

  const { groups, locations } = useGroupedLocations();
  const { timeFrequencies } = useTimeFrequency();
  const { models } = useModelOptions();
  const { variables } = useVariableOptions();
  const { dates } = useForecastDateOptions();
  const { members: memberOptions } = useMemberOptions();
  const { preferences, isLoading: preferencesLoading } = useListPreferences();
  const { selectPreference } = useSelectPreference();

  const [autoFilling, setAutoFilling] = useState(false);
  // Decisão one-shot por modo: depois do primeiro despacho o modo deixa de ser
  // pristine, então sem esta trava o efeito se auto-desarmaria no render seguinte.
  const decidedSubpage = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (decidedSubpage.current === subpage) return;
    if (preferencesLoading) return;
    decidedSubpage.current = subpage;

    const pristine = isPristine({
      locationId, multilocationIds, multimodelIds, members, rounds, variable, forecastDate,
    }, subpage);
    // Usuário já escolheu algo neste modo: não sobrescrever.
    if (!pristine) return;

    const [preference] = preferences;
    if (preference) {
      // Preferência salva tem precedência sobre o fallback.
      selectPreference(preference)();
      return;
    }

    setAutoFilling(true);
    // Sinaliza a auto-consulta em curso já no início da cascata: o gráfico mostra
    // o spinner durante todas as requisições em série, não só na consulta final
    // (#1822). Desliga no efeito do triggerRequest, junto com `autoFilling`.
    store.dispatch(setAutoQuerying(true));
  }, [
    subpage, preferencesLoading, preferences, selectPreference,
    locationId, multilocationIds, multimodelIds, members, rounds, variable, forecastDate,
  ]);

  useEffect(() => {
    if (!autoFilling || timeFrequency !== undefined) return;
    const value = resolveTimeFrequency(timeFrequencies);
    if (value) store.dispatch(setTimeFrequency(value));
  }, [autoFilling, timeFrequency, timeFrequencies]);

  useEffect(() => {
    if (!autoFilling || !locations.length) return;

    if (subpage === 'locations') {
      if (multilocationIds.length) return;
      store.dispatch(setMultiGroupIds(allGroupIds(groups)));
      store.dispatch(setMultiLocationIds(allLocationIds(locations)));
      return;
    }

    if (locationId !== undefined) return;
    const location = firstLocationOfFirstGroup(groups, locations);
    if (location) store.dispatch(setLocationId(location.id));
  }, [autoFilling, subpage, groups, locations, multilocationIds, locationId]);

  useEffect(() => {
    if (!autoFilling) return;

    if (subpage === 'models') {
      if (multimodelIds.length) return;
      const id = resolveModelId(models);
      if (id === undefined) return;
      store.dispatch(setMultiModelIds([id]));
      return;
    }

    // modelId é compartilhado entre os modos de modelo único e sobrevive à troca de
    // aba (só `setSubpage`, nunca `resetCP`). Mantém o valor herdado apenas se ele
    // existir na lista DESTE modo. O Multi-Membros não lista o INDICACAOMET (default
    // dos outros modos — não tem membros de ensemble), então um modelId herdado do
    // Multi-Locais é inválido aqui: sem re-seleção o rádio fica sem marcação e a
    // cascata trava (sem modelo, a query de membros e a consulta nunca disparam).
    if (models.some((m) => m.id === modelId)) return;
    // Fallback só do Multi-Membros: quando o INDICACAOMET não está na lista, cai no
    // primeiro modelo de ensemble disponível.
    const id = subpage === 'members'
      ? resolveModelId(models) ?? firstModelId(models)
      : resolveModelId(models);
    if (id === undefined) return;
    store.dispatch(setModelId(id));
  }, [autoFilling, subpage, models, modelId, multimodelIds]);

  useEffect(() => {
    if (!autoFilling || subpage !== 'members') return;
    // Espera o modelo virar um id válido DESTE modo antes de preencher os membros.
    // Na troca de aba, o modelId herdado (ex.: INDICACAOMET do Multi-Locais) ainda
    // não pertence à lista de ensemble e o `memberOptions` desse render são os do
    // modelo errado — preencher agora dispararia a consulta vazia (#1822). Quando o
    // efeito de modelo re-seleciona, `modelId`/`models` mudam e este efeito re-roda.
    if (!models.some((m) => m.id === modelId)) return;
    if (members.length || !memberOptions.length) return;
    store.dispatch(setMembers(allMemberIds(memberOptions)));
  }, [autoFilling, subpage, members, memberOptions, models, modelId]);

  useEffect(() => {
    if (!autoFilling || variable !== undefined) return;
    const value = resolveVariable(variables);
    if (value) store.dispatch(setVariable(value));
  }, [autoFilling, variable, variables]);

  useEffect(() => {
    if (!autoFilling) return;
    // Rodadas/Membros: a lista inclui a rodada de HOJE sem dados ainda — abrir nela
    // dava gráfico vazio (#1822). Nesses modos o default pula hoje pra data anterior;
    // Locais/Modelos seguem na mais recente (a lista deles não traz hoje sem dado).
    const skipsToday = subpage === 'rounds' || subpage === 'members';
    const date = skipsToday
      ? mostRecentAvailableDate(dates, format(new Date(), 'yyyy-MM-dd'))
      : mostRecentDate(dates);
    if (!date) return;

    if (subpage === 'rounds') {
      if (rounds.length) return;
      store.dispatch(setRounds([date]));
      return;
    }

    if (forecastDate) return;
    store.dispatch(setForecastDate(date));
  }, [autoFilling, subpage, dates, rounds, forecastDate]);

  // Auto-consulta: `triggerRequest` só fica definido quando os params do modo
  // estão completos (ver useChartRequestTrigger), então isto é o fim da cascata.
  useEffect(() => {
    if (!autoFilling || !triggerRequest) return;
    triggerRequest();
    setAutoFilling(false);
    // Fim da cascata: a consulta final disparou e agora tem seu próprio isFetching.
    store.dispatch(setAutoQuerying(false));
  }, [autoFilling, triggerRequest]);
}
